// styled.ts — styled-components de ModPage (Prompt Playground): layout de dos
// paneles, config bar, secciones editables de prompts, resultados de generación
// y status. Extraídos de la página para mantenerla bajo el cap; son CSS-data
// (excepción). Sin lógica.
import styled from 'styled-components';

export const PageWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    height: auto;
    overflow: visible;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

export const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

export const MainLayout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  flex: 1;
  min-height: 0;

  @media (max-width: 900px) {
    flex-direction: column;
    min-height: auto;
  }
`;

// Left panel: config + actions + results (scrollable)
export const LeftPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 900px) {
    overflow: visible;
    padding-right: 0;
  }
`;

// Right panel: editable sections (scrollable, fixed width)
export const RightPanel = styled.div`
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-left: ${({ theme }) => theme.borders.thin};
  padding-left: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 900px) {
    width: 100%;
    border-left: none;
    border-top: ${({ theme }) => theme.borders.thin};
    padding-left: 0;
    padding-top: ${({ theme }) => theme.spacing.lg};
  }
`;

export const PanelTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

export const ConfigBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

export const ConfigLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

export const Select = styled.select`
  padding: 6px 10px;
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

export const CountInput = styled.input`
  width: 60px;
  padding: 6px 10px;
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
`;

export const NetworkCheckboxes = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
`;

export const NetworkChip = styled.label<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $active }) => $active ? theme.colors.primary + '15' : 'transparent'};
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;

  input { display: none; }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const SectionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  min-height: 0;
`;

export const SectionCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }
`;

export const SectionTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

export const SectionBadge = styled.span<{ $modified?: boolean }>`
  font-size: 10px;
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme, $modified }) => $modified ? theme.colors.warning + '30' : theme.colors.surfaceHover};
  color: ${({ theme, $modified }) => $modified ? theme.colors.warning : theme.colors.textMuted};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

export const SectionBody = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => $open ? 'block' : 'none'};
  padding: ${({ theme }) => theme.spacing.sm};
`;

export const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 12px;
  font-family: 'Courier New', Consolas, monospace;
  line-height: 1.4;
  resize: vertical;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ActionBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

export const ResultsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const ResultsTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

export const ProviderBadge = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.secondary}20;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

export const PostCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const PostMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

export const MetaTag = styled.span<{ $color?: string }>`
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ $color }) => ($color || '#3B82F6') + '18'};
  color: ${({ $color }) => $color || '#3B82F6'};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

export const PostContent = styled.pre`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

export const DynamicTextsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const DtItem = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: ${({ theme }) => theme.borders.thin};
`;

export const DtLabel = styled.div`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

export const DtValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

export const PromptPreview = styled.pre`
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: 12px;
  font-family: 'Courier New', Consolas, monospace;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const StatusBar = styled.div<{ $type?: 'info' | 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-shrink: 0;
  background: ${({ theme, $type }) =>
    $type === 'error' ? theme.colors.error + '15'
    : $type === 'success' ? theme.colors.success + '15'
    : theme.colors.primary + '10'};
  color: ${({ theme, $type }) =>
    $type === 'error' ? theme.colors.error
    : $type === 'success' ? theme.colors.success
    : theme.colors.primary};
  border: 1px solid ${({ theme, $type }) =>
    $type === 'error' ? theme.colors.error + '30'
    : $type === 'success' ? theme.colors.success + '30'
    : theme.colors.primary + '20'};
`;

export const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

export const ResetLink = styled.button`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

export const SaveBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.md};
  margin-top: auto;
  flex-shrink: 0;
  border-top: ${({ theme }) => theme.borders.thin};
`;
